import {PropsWithChildren, ReactNode, useEffect, useState} from 'react';
import ApplicationLogo from '@/Components/ApplicationLogo';
import NavLink from '@/Components/NavLink';
import ResponsiveNavLink from '@/Components/ResponsiveNavLink';
import {Link, usePage} from '@inertiajs/react';
import {PageProps} from '@/types';
import {faFileLines, faUser} from "@fortawesome/free-regular-svg-icons";
import {faArrowRightFromBracket, faCircleQuestion, faCode, faGear, faMoon, faProjectDiagram} from "@fortawesome/free-solid-svg-icons";
import {Toaster} from "react-hot-toast";
import useSessionToast from "@/utils/useSessionToast";
import * as Sentry from "@sentry/react";
import {setUserData} from "@/services/analytics";
import Footer from "@/Layouts/Auth/Footer";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {resetDarkMode, useToggleDarkMode} from "@/hooks/useDarkMode";
import UserProfileDropdown from '@/Components/UserProfileDropdown';
import usePaymentFlash from '@/hooks/usePaymentFlash';

export default function Authenticated({
    header = null,
    logo,
    children,
    mainClassName,
    containerClassName = "min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900",
    showFooter = true,
    showMobileMenu = true,
    mobileMenuExtras
}: PropsWithChildren<{
    header?: ReactNode,
    logo?: ReactNode,
    mobileMenuExtras?: (closeMenu: () => void) => ReactNode,
    mainClassName?: string
    showFooter?: boolean,
    showMobileMenu?: boolean,
    containerClassName?: string
}>) {
    const [showingNavigationDropdown, setShowingNavigationDropdown] = useState(false);

    // The drawer sits under the top bar, which scrolls with the page, so jump to the top before opening
    const toggleMobileMenu = () => {
        if (!showingNavigationDropdown) {
            window.scrollTo({top: 0});
        }
        setShowingNavigationDropdown(previousState => !previousState);
    };
    const {isDark, toggle: toggleDarkMode} = useToggleDarkMode();
    const {sessionFlash, auth} = usePage<PageProps>().props

    useSessionToast(sessionFlash);
    usePaymentFlash(sessionFlash);

    useEffect(() => {
        setUserData(auth.user);
        Sentry.setUser({id: auth.user.id})
    }, []);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (showingNavigationDropdown) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showingNavigationDropdown]);

    return (
        <div className={containerClassName}>
            <nav className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        <div className="flex">

                            {
                                logo ? <div className="flex items-center">{logo}</div> : (
                                    <div className="shrink-0 flex items-center">
                                        <Link href="/">
                                            <ApplicationLogo className="block h-9 w-auto fill-current text-gray-800 dark:text-gray-200"/>
                                        </Link>
                                    </div>
                                )
                            }

                            <div className="hidden space-x-8 sm:-my-px sm:ms-10 sm:flex">
                                <NavLink href={route('dashboard')} active={route().current('dashboard')}>
                                    Dashboard
                                </NavLink>
                                <NavLink href={route('workflows.index')} active={route().current('workflow*')}>
                                    Workflows
                                </NavLink>
                                <NavLink href={route('api-info')} active={route().current('api*')}>
                                    API
                                </NavLink>
                                <NavLink href={route('contact')} active={route().current('contact')}>
                                    Support
                                </NavLink>
                            </div>
                        </div>

                        <div className="hidden sm:flex sm:items-center sm:ms-6">
                            <div className="ms-3">
                                <UserProfileDropdown user={auth.user} variant="icon" />
                            </div>
                        </div>

                        { showMobileMenu && (
                            <div className="-me-2 flex items-center sm:hidden">
                                <button
                                    onClick={toggleMobileMenu}
                                    className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700 focus:text-gray-500 dark:focus:text-gray-200 transition duration-150 ease-in-out"
                                >
                                    <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                        <path
                                            className={!showingNavigationDropdown ? 'inline-flex' : 'hidden'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M4 6h16M4 12h16M4 18h16"
                                        />
                                        <path
                                            className={showingNavigationDropdown ? 'inline-flex' : 'hidden'}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>


                <div
                    onClick={() => setShowingNavigationDropdown(false)}
                    className={`fixed z-40 inset-x-0 top-16 bottom-0 bg-black bg-opacity-50 transition-opacity duration-300 sm:hidden ${
                        showingNavigationDropdown ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                />


                <div
                    className={`fixed z-50 top-16 bottom-0 left-0 w-64 flex flex-col bg-white shadow-[inset_0_6px_6px_-6px_rgba(0,0,0,0.15)] dark:bg-gray-800 dark:shadow-[inset_0_6px_6px_-6px_rgba(0,0,0,0.6)] transform transition-transform duration-300 ease-in-out sm:hidden ${
                        showingNavigationDropdown ? 'translate-x-0' : '-translate-x-full'
                    }`}
                >
                    <div className="pt-3 pb-1">
                        <div className="space-y-1">
                            <ResponsiveNavLink href={route('profile.edit')} active={route().current('profile*')}>
                                <FontAwesomeIcon className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                                                 icon={faUser}/> Profile
                            </ResponsiveNavLink>
                            <ResponsiveNavLink href={route('billing.credits')} active={route().current('billing*')}>
                                <FontAwesomeIcon className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                                                 icon={faFileLines}/> Billing
                            </ResponsiveNavLink>
                            <ResponsiveNavLink href={route('setting.edit')} active={route().current('setting*')}>
                                <FontAwesomeIcon className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                                                 icon={faGear}/> Settings
                            </ResponsiveNavLink>
                            <ResponsiveNavLink href={route('workflows.index')} active={route().current('workflows*')}>
                                <FontAwesomeIcon className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                                                 icon={faProjectDiagram}/> Workflows
                            </ResponsiveNavLink>
                            <ResponsiveNavLink href={route('api-info')} active={route().current('api*')}>
                                <FontAwesomeIcon className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center" icon={faCode}/> API
                            </ResponsiveNavLink>
                            <ResponsiveNavLink href={route('contact')} active={route().current('contact')}>
                                <FontAwesomeIcon className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                                                 icon={faCircleQuestion}/> Support
                            </ResponsiveNavLink>
                            <div
                                className="flex items-center justify-between w-full ps-3 pe-4 py-2 border-l-4 border-transparent cursor-pointer text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition duration-150 ease-in-out"
                                onClick={toggleDarkMode}
                            >
                                <div className="flex items-center text-base font-medium">
                                    <FontAwesomeIcon
                                        className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3"
                                        icon={faMoon}
                                    />
                                    <span>Dark Mode</span>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={isDark}
                                    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                        isDark ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                                    }`}
                                >
                                    <span className="sr-only">Dark Mode</span>
                                    <span
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            isDark ? 'translate-x-4' : 'translate-x-0'
                                        }`}
                                    />
                                </button>
                            </div>
                            <ResponsiveNavLink
                                method="post"
                                href={route('logout')}
                                as="button"
                                onSuccess={resetDarkMode}
                            >
                                <FontAwesomeIcon className="text-gray-500 dark:text-gray-400 w-4 h-4 pr-3 self-center"
                                                 icon={faArrowRightFromBracket}/> Log Out
                            </ResponsiveNavLink>
                        </div>
                    </div>
                    {mobileMenuExtras && (
                        <div className="mt-auto border-t px-4 pb-4 pt-4 dark:border-gray-700">
                            {mobileMenuExtras(() => setShowingNavigationDropdown(false))}
                        </div>
                    )}
                </div>
            </nav>

            {header ? (
                header
            ) : <div className="h-10"></div>}

            <main className={mainClassName}>
                {children}
                {showFooter && <Footer/>}
            </main>
            <Toaster
                toastOptions={{ className: 'dark:bg-gray-800 dark:text-white' }}
            />
        </div>
    );
}
